import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { StageActions } from 'src/app/modules/dashboard/interface/actions-response';
import { IEPResponse } from 'src/app/modules/dashboard/interface/dashboard-response';
import { IAssessmentPlanning } from 'src/app/modules/evaluations/interfaces/assessment-planning';
import { EvalSubmit } from 'src/app/modules/evaluations/models/assessment-planning/assessment-planning-save.model';
import { EvaluationService } from 'src/app/modules/evaluations/services/evaluation.service';
import { StudentService } from 'src/app/services/student.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { CapatureFirstAttendResponse } from 'src/app/shared/interfaces/capture-first-attend';
import { FirstAttendDateRequest } from 'src/app/shared/interfaces/first-attend-date';
import { ISedmsFormOutput } from 'src/app/shared/interfaces/form/sedms-form-output.model';
import {
  Attendees,
  IMeetingDetails,
} from 'src/app/shared/interfaces/meeting-details';
import { StudentProfile } from 'src/app/shared/interfaces/student';
import { StudentDetails } from 'src/app/shared/interfaces/student-details';
import { AppConfigService } from '../../../../services/app-config.service';
import { IAppConfig } from '../../../../shared/interfaces/app-config';
import { ICalculatedEndDateRequest } from '../interfaces/calculated-end-date';
import { CBSTRequest } from '../interfaces/cbst';
import { Compliance } from '../interfaces/compliance';
import { EducationalBenefitReviewResponse } from '../interfaces/educational-benefit-review';
import { EligibilityRequest } from '../interfaces/eligibility';
import { IepMeeting } from '../interfaces/iep-meeting';
import { IEPProgramServicesKeyValuePairModel } from '../interfaces/iep-program-service';
import { IIepSectionContributor } from '../interfaces/iep-section-contributor';
import { IepTeam } from '../interfaces/iep-team';
import { IIepType } from '../interfaces/iep-type';
import { ILock } from '../interfaces/lock';
import { ILockRequest } from '../interfaces/lock-request';
import { IepDetailsResponse } from '../interfaces/previous-iep-details';
import {
  IepProgressReport,
  ProgressReportDetail,
  ProgressReportRequest,
} from '../interfaces/progress-report';
import { SpecialTransportationRequest } from '../interfaces/special-transportation';
import { AttendancePageRequest } from '../models/attendance-page-models/attendance-page-request-model';
import { PrintProgressReportModel } from '../models/iep-progress-to-parents-models/iep-progress-print-model';
import { AddProgramServicesRequest } from '../models/iep-recommended-program-and-service-models/add-program-services.model';
import { PreparerInformation } from '../interfaces/lre-form';
import { EligibilityResponse } from 'src/app/modules/dashboard/interface/eligibility-response';

@Injectable({
  providedIn: 'root',
})
export class IEPService<T extends ISedmsFormOutput> {
  config: IAppConfig;
  iepType: Subject<IIepType> = new Subject<IIepType>();
  private iepMeeting: BehaviorSubject<IepMeeting> =
    new BehaviorSubject<IepMeeting>(null);
  public iepMeeting$: Observable<IepMeeting> = this.iepMeeting.asObservable();
  iepSave: Subject<string> = new Subject<string>();
  private contributors: BehaviorSubject<IIepSectionContributor[]> =
    new BehaviorSubject<IIepSectionContributor[]>([]);
  public contributors$: Observable<IIepSectionContributor[]> =
    this.contributors.asObservable();

  public componentLoaded: BehaviorSubject<FormGroup> =
    new BehaviorSubject<FormGroup>(null);
  componentLoadedData$: Observable<FormGroup> =
    this.componentLoaded.asObservable();

  private eligibilityStatusSubject: BehaviorSubject<number | null> =
    new BehaviorSubject<number | null>(null);
  eligibilityStatus$: Observable<number | null> =
    this.eligibilityStatusSubject.asObservable();

  public selectedDisabilitySubject: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  selectedDisability$: Observable<number | null> = this.selectedDisabilitySubject.asObservable();

  public selectedStudent: StudentProfile | StudentDetails;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private evaluationService: EvaluationService<EvalSubmit>,
    private studentService: StudentService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  // TODO: Requires a lot of changes to strongly type as this is used in a LOT of components, leaving as any for now.
   
  public submit(request: T, type: string): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/${type}/Add`, request)
      .pipe(catchError(handleError));
  }
   
  public submitAssuranceCheckList(request: T, type: string): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/${type}/AddAssuranceWorklist`, request)
      .pipe(catchError(handleError));
  }
   
  public submitCbstApproval(request: T, type: string): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/${type}/CBSTAprroval`, request)
      .pipe(catchError(handleError));
  }
  public amend(
    request: T,
    type: string
  ): Observable<ApiResponse<EligibilityRequest>> {
    return this.http
      .post<ApiResponse<EligibilityRequest>>(
        `${this.config.apiUrl}/${type}/Amend`,
        request
      )
      .pipe(catchError(handleError));
  }
  public submitMeeting(
    request: T,
    type: string
  ): Observable<ApiResponse<IMeetingDetails>> {
    return this.http
      .post<ApiResponse<IMeetingDetails>>(
        `${this.config.studentApiUrl}/${type}/Add`,
        request
      )
      .pipe(catchError(handleError));
  }
  public updateMeeting(
    request: T,
    type: string
  ): Observable<ApiResponse<IMeetingDetails>> {
    return this.http
      .post<ApiResponse<IMeetingDetails>>(
        `${this.config.studentApiUrl}/${type}/Update`,
        request
      )
      .pipe(catchError(handleError));
  }
  public updateMeetingAttendees(
    request: T,
    type: string
  ): Observable<AttendancePageRequest> {
    return this.http
      .post<AttendancePageRequest>(
        `${this.config.studentApiUrl}/${type}/UpdateAttendees`,
        request
      )
      .pipe(catchError(handleError));
  }

  public loadAllContributors(iepFormSk: string): void {
    this.http
      .get<IIepSectionContributor[]>(
        `${this.config.apiUrl}/Contributor/GetIEPContributors/${iepFormSk}`
      )
      .pipe(catchError(handleError))
      .subscribe((data: IIepSectionContributor[]) => {
        this.contributors.next(data);
      });
  }

  public isLocked(lockRequest: ILockRequest): Observable<ILock> {
    return this.http
      .get<ILock>(
        `${this.config.apiUrl}/Contributor/GetLock/${lockRequest.iepFormSk}/${lockRequest.sectionID}/${lockRequest.userName}`
      )
      .pipe(catchError(handleError));
  }

  public releaseLock(lockRequest: ILockRequest): Observable<number> {
    return this.http
      .get<number>(
        `${this.config.apiUrl}/Contributor/ReleaseLock/${lockRequest.iepFormSk}/${lockRequest.sectionID}/${lockRequest.userName}`
      )
      .pipe(catchError(handleError));
  }

   
  public get(id: string, type: string): Observable<any> {
     
    return this.http.get<any[]>(`${this.config.apiUrl}/${type}/Get/` + id).pipe(
       
      map((res: any) => res?.value || res),
      catchError(handleError)
    );
  }

  public getWithFlag(
    id: string,
    type: string,
    flagName: string,
    flag: boolean
  ): Observable<AddProgramServicesRequest> {
    return this.http
      .get<AddProgramServicesRequest>(
        `${this.config.apiUrl}/${type}/Get/` + `${id}?${flagName}=${flag}`
      )
      .pipe(catchError(handleError));
  }

  public getMeetingDetails(
    id: string,
    type: string
  ): Observable<IMeetingDetails> {
    return this.http
      .get<IMeetingDetails>(`${this.config.studentApiUrl}/${type}/Get/` + id)
      .pipe(catchError(handleError));
  }

  public getDetails(
    id: string,
    type: string,
    formtype: number
  ): Observable<ApiResponse<CBSTRequest>> {
    return this.http
      .get<ApiResponse<CBSTRequest>>(
        `${this.config.apiUrl}/${type}/Get/` + id + '/' + formtype
      )
      .pipe(catchError(handleError));
  }

  public getAssessmentsForIEP(iepformsk: string): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.config.apiUrl}/CBST/GetAssessments/` + iepformsk)
      .pipe(catchError(handleError));
  }

  public getProgramsAndServices(
    iepformsk: string
  ): Observable<IEPProgramServicesKeyValuePairModel> {
    return this.http
      .get<IEPProgramServicesKeyValuePairModel>(
        `${this.config.apiUrl}/IEPProgramServices/GetProgramServices/` +
          iepformsk
      )
      .pipe(catchError(handleError));
  }

  public getSEITApplicableForPAL(studentSk: string): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('studentSk', studentSk);
    return this.http
      .get<boolean>(
        `${this.config.apiUrl}/IEPProgramServices/GetSEITApplicableForPAL/`,
        { params }
      )
      .pipe(catchError(handleError));
  }

  public getProgramsAndServicesByNycId(nycid: string): Observable<IEPResponse> {
    return this.http
      .get<IEPResponse>(
        `${this.config.apiUrl}/IEPProgramServices/GetStudentIEP/` + nycid
      )
      .pipe(catchError(handleError));
  }

  getEligibility(iepSK: string): Observable<EligibilityResponse> {
    return this.http.get<EligibilityResponse>(
      `${this.config.apiUrl}/IEP/Get/${iepSK}`
    );
  }

  public getCaptureFirstDateByNycId(
    nycid: string
  ): Observable<CapatureFirstAttendResponse> {
    return this.http
      .get<CapatureFirstAttendResponse>(
        `${this.config.apiUrl}/PlacementFinder/GetFirstAttendenceDeatils?studentId=${nycid}`
      )
      .pipe(catchError(handleError));
  }

  public getIEPTeam(nycid: string, dbn: string): Observable<IepTeam> {
    return this.http
      .get<IepTeam>(
        `${this.config.apiUrl}/IEPProgramServices/getIEPTeam/${nycid}/${dbn}`
      )
      .pipe(catchError(handleError));
  }

  public getMiscDetails(studentSk: string): Observable<StageActions[]> {
    return this.http
      .get<StageActions[]>(
        `${this.config.apiUrl}/Dashboard/GetMiscInfo/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getExistingAttendanceList(iepformsk: string): Observable<Attendees> {
    return this.http
      .get<Attendees>(
        `${this.config.studentApiUrl}/Meeting/GetAttendees/` + iepformsk
      )
      .pipe(catchError(handleError));
  }

  public getMeeting(meetingsk: string): Observable<string> {
    return this.http
      .get<string>(`${this.config.studentApiUrl}/Meeting/Get/` + meetingsk)
      .pipe(catchError(handleError));
  }

  public getComplianceDateForIep(
    id: string,
    isReferral: boolean
  ): Observable<Compliance> {
    return this.http
      .get<Compliance>(
        `${this.config.apiUrl}/IEP/GetComplianceDate?iepFormsk=${id}&isReferral=${isReferral}`
      )
      .pipe(catchError(handleError));
  }

  public getComplianceDateForcaseclose(
    id: string,
    isReferral: boolean
  ): Observable<Compliance> {
    return this.http
      .get<Compliance>(
        `${this.config.apiUrl}/IEP/GetComplianceDateCaseClose?iepFormsk=${id}&isReferral=${isReferral}`
      )
      .pipe(catchError(handleError));
  }

  public submitAttendanceDocument(
    id: string,
    request: Attendees
  ): Observable<Uint8Array> {
    return this.http
      .post<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/AttendanceDocument?meetingSk=${id}`,
        request
      )
      .pipe(catchError(handleError));
  }

  public submitCaptureFirstAttendDate(
    request: FirstAttendDateRequest[]
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.config.apiUrl}/PlacementFinder/CaptureFirstAttendDate`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getPastIEPs(studentSk: string): Observable<IepProgressReport[]> {
    return this.http
      .get<IepProgressReport[]>(
        `${this.config.apiUrl}/ProgressReport/GetIEPByStudent/${studentSk}`
      )
      .pipe(catchError(handleError));
  }

  public getProgressReport(
    progressReportSK: string
  ): Observable<ProgressReportDetail> {
    return this.http
      .get<ProgressReportDetail>(
        `${this.config.apiUrl}/ProgressReport/GetProgressReport/${progressReportSK}`
      )
      .pipe(catchError(handleError));
  }

  getSpecialTransportationServiceDetails(
    iepFormSk: string
  ): Observable<ApiResponse<SpecialTransportationRequest>> {
    return this.http.get<ApiResponse<SpecialTransportationRequest>>(
      `${this.config.apiUrl}/SpecialTransportation/Get/${iepFormSk}`
    );
  }

  public getGoalsForNewProgressReport(
    iepFormSk: string
  ): Observable<ProgressReportDetail> {
    return this.http
      .get<ProgressReportDetail>(
        `${this.config.apiUrl}/ProgressReport/GetGoalsForNewProgressReport/${iepFormSk}`
      )
      .pipe(catchError(handleError));
  }

  public updateProgressReportGoal(
    goal: ProgressReportRequest
  ): Observable<ApiResponse<ProgressReportRequest>> {
    return this.http
      .post<ApiResponse<ProgressReportRequest>>(
        `${this.config.apiUrl}/ProgressReport/Add`,
        goal
      )
      .pipe(catchError(handleError));
  }

  public getTransport(
    id: string
  ): Observable<ApiResponse<SpecialTransportationRequest>> {
    return this.http
      .get<ApiResponse<SpecialTransportationRequest>>(
        `${this.config.apiUrl}/SpecialTransportation/Get/${id}`
      )
      .pipe(catchError(handleError));
  }

  public getPreparerInformation(): Observable<PreparerInformation> {
    return this.http
      .get<PreparerInformation>(`${this.config.apiUrl}/CBST/PreparerDetailes`)
      .pipe(catchError(handleError));
  }

  public getEducationalBenefitReviewProcess(
    id: string
  ): Observable<EducationalBenefitReviewResponse> {
    return this.http
      .get<EducationalBenefitReviewResponse>(
        `${this.config.apiUrl}/EducationalBenefitReviewProcess/Get/IEPForEdBenefitReview/` +
          id
      )
      .pipe(
        map((res: EducationalBenefitReviewResponse) => res),
        catchError(handleError)
      );
  }

  public getMeetingDate(
    iepFormSk: string
  ): Observable<ApiResponse<EligibilityRequest>> {
    return this.http
      .get<ApiResponse<EligibilityRequest>>(
        `${this.config.apiUrl}/IEPConsent/GetMeeting/${iepFormSk}`
      )
      .pipe(catchError(handleError));
  }

  public getCalculatedEndDate(
    request: ICalculatedEndDateRequest
  ): Observable<string> {
    return this.http
      .post<string>(
        `${this.config.apiUrl}/Evaluations/GetCalculatedEndDate`,
        request
      )
      .pipe(catchError(handleError));
  }

  public printProgressReport(
    request: PrintProgressReportModel,
    iepFormSK: string,
    studentSk: string
  ): Observable<Uint8Array> {
    return this.http
      .post<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/ProgressReport?Iepformsk=${iepFormSK}&StudentSk=${studentSk}`,
        request
      )
      .pipe(catchError(handleError));
  }

  public previewDocument(iepFormSk: string): Observable<Uint8Array> {
    return this.http
      .get<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/PreviewIEPDocument?Iepformsk=${iepFormSk}`
      )
      .pipe(catchError(handleError));
  }

  public getPlannedAssessments(nycId: string): Observable<IAssessmentPlanning> {
    return this.studentService.studentDetailsById(nycId).pipe(
       
      take(1),
      switchMap((response: StudentDetails) => {
        return this.evaluationService.getAssessmentPlanning(
          response?.studentSk
        );
      })
    );
  }

  public getPreviousIepDetails(
    studentSk: string
  ): Observable<IepDetailsResponse> {
    const params: HttpParams = new HttpParams().set('studentSk', studentSk);
    return this.http
      .get<IepDetailsResponse>(
        `${this.config.apiUrl}/IEPProgramServices/GetPreviousiepDetails`,
        { params }
      )
      .pipe(catchError(handleError));
  }

  public setEligibilityStatus(iepStatus: number): void {
    this.eligibilityStatusSubject.next(iepStatus);
  }

  public updateIepSave(iepSave: string): void {
    this.iepSave.next(iepSave);
  }

  public getIepSave(): Observable<string> {
    return this.iepSave.asObservable();
  }

  public submitUpdateFirstAttendDate(request: FirstAttendDateRequest): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.config.apiUrl}/IEPProgramServices/InsertorUpdateFirstAttendDate`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getProgressReports(iepFormSk: string): Observable<IepProgressReport[]> {
    return this.http
      .get<IepProgressReport[]>(
        `${this.config.apiUrl}/ProgressReport/GetProgressReportWithDate/${iepFormSk}`
      )
      .pipe(catchError(handleError));
  }

  public getScheduleMeetingList(id: string): Observable<IMeetingDetails[]> {
    return this.http
      .get<IMeetingDetails[]>(`${this.config.studentApiUrl}/Meeting/GetMeetingList/${id}`)
      .pipe(catchError(handleError));
  }

  setSelectedDisability(disabilityId: number | null): void {
    this.selectedDisabilitySubject.next(disabilityId);
  }

  public onChangeIepType(request: IIepType): Observable<ApiResponse<IIepType>> {
    return this.http
      .post<ApiResponse<IIepType>>(`${this.config.apiUrl}/IEP/OnchangeIEP`, request)
      .pipe(catchError(handleError));
  }
  public addIepTypeAsCSP(request: T): Observable<ApiResponse<T>> {
    return this.http
      .post<ApiResponse<T>>(`${this.config.apiUrl}/IEP/Add`, request)
      .pipe(catchError(handleError));
  }

  public IEPMeasuableAnnualGoal(request: T): Observable<ApiResponse<T>> {
    return this.http
      .post<ApiResponse<T>>(`${this.config.apiUrl}/IEPMeasuableAnnualGoal/Add`, request)
      .pipe(catchError(handleError));
  }

  public updateIepType(iepType: IIepType): void {
    this.iepType.next(iepType);
  }

  public updateIepMeeting(iepMeeting: IepMeeting): void {
    this.iepMeeting.next(iepMeeting);
  }

  public getIepType(): Observable<IIepType> {
    return this.iepType.asObservable();
  }
}
